/**
 * TrialBanner - Bannière countdown pour le trial 14 jours
 * Affichée dans le dashboard pendant la période d'essai
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Sparkles, X, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionsAPI } from '@/utils/api';

interface TrialBannerProps {
  onDismiss?: () => void;
  compact?: boolean;
}

export default function TrialBanner({ onDismiss, compact = false }: TrialBannerProps) {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  // Récupérer l'abonnement courant
  // Même queryKey que useTrialGuard pour partager le cache
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: () => subscriptionsAPI.getCurrent(),
    staleTime: 30 * 1000, // Cache 30 secondes
    retry: 1,
    refetchOnWindowFocus: true,
  });

  // Calculer les jours restants
  const { daysRemaining, isExpired, isTrialPlan } = useMemo(() => {
    if (!subscription) {
      return { daysRemaining: 0, isExpired: false, isTrialPlan: false };
    }

    // Vérifier si c'est un plan TRIAL
    const planCode = (subscription as any).plan?.code || (subscription as any).planId || '';
    const isTrialPlan = planCode.toUpperCase().includes('TRIAL');

    if (!isTrialPlan || !subscription.trialEndsAt) {
      return { daysRemaining: 0, isExpired: false, isTrialPlan: false };
    }

    const trialEnd = new Date(subscription.trialEndsAt);
    const now = new Date();
    const diffMs = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return {
      daysRemaining: Math.max(0, diffDays),
      isExpired: diffDays <= 0,
      isTrialPlan: true,
    };
  }, [subscription]);

  // Ne pas afficher si pas en trial, chargement, ou déjà dismissé
  if (isLoading || !isTrialPlan || isDismissed) {
    return null;
  }

  // Si trial expiré, afficher un message d'expiration
  if (isExpired) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 animate-pulse" />
            <span className="font-medium">
              Votre période d'essai est terminée. Choisissez un plan pour continuer.
            </span>
          </div>
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 bg-white text-red-600 px-4 py-1.5 rounded-lg font-semibold hover:bg-red-50 transition-colors"
          >
            Voir les plans
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Mode compact (une ligne)
  if (compact) {
    return (
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <Sparkles className="h-5 w-5" />
            <span className="text-lg">
              <strong>{daysRemaining} jour{daysRemaining > 1 ? 's' : ''}</strong> restants dans votre essai gratuit
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center gap-2 bg-white text-teal-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-teal-50 transition-colors shadow-lg text-base"
            >
              Choisir un plan
              <ArrowRight className="h-4 w-4" />
            </button>
            {onDismiss && (
              <button
                onClick={() => {
                  setIsDismissed(true);
                  onDismiss?.();
                }}
                className="text-teal-200 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mode standard (avec plus de détails)
  const urgencyClass = daysRemaining <= 3
    ? 'from-orange-500 to-red-500'
    : daysRemaining <= 7
      ? 'from-amber-500 to-orange-500'
      : 'from-teal-500 to-teal-600';

  return (
    <div className={`bg-gradient-to-r ${urgencyClass} text-white px-4 py-4`}>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg">
              {daysRemaining <= 3 ? (
                <>Plus que {daysRemaining} jour{daysRemaining > 1 ? 's' : ''} d'essai !</>
              ) : (
                <>Essai gratuit - {daysRemaining} jours restants</>
              )}
            </h3>
            <p className="text-white/90 text-sm mt-0.5">
              {daysRemaining <= 3 ? (
                <>Profitez de toutes les fonctionnalités avant la fin de votre période d'essai.</>
              ) : (
                <>Vous bénéficiez d'un accès complet à toutes les fonctionnalités pendant 14 jours.</>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate('/pricing')}
            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-white text-teal-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-teal-50 transition-colors shadow-lg"
          >
            Choisir un plan
            <ArrowRight className="h-4 w-4" />
          </button>
          {onDismiss && (
            <button
              onClick={() => {
                setIsDismissed(true);
                onDismiss?.();
              }}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
